import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { HeaderComponent } from "@components/header/header.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { InputComponent } from "@components/input/input.component";
import { SaveButtonComponent } from "@components/save-button/save-button.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";
import { switchMap, tap } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { CheckboxComponent } from "~app/common/components/checkbox/checkbox.component";
import { ToastService } from "~app/common/components/toast/toast.service";
import { MarknadsprislistaTyp } from "~app/common/models/constants";
import { ProduktionsomradeDetailDTO } from "~app/common/models/produktionsomrade/ProduktionsomradeDetailDTO";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { DirtyFormService, IDirtyFormResult } from "~app/common/services/dirty-form.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { StringUtils } from "~app/common/utils/string-utils";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { IValidationScope } from "~app/common/validation/IValidationScope";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { IMarknadsprislista } from "~app/features/virkeskop/models/marknadsprislista-model";
import { MarknadsprislistaService } from "~app/features/virkeskop/services/marknadsprislista.service";
import { ProduktionsomradeService } from "../produktionsomrade.service";
import { ProduktionsomradeTexts } from "../produktionsomrade.texts";
import { ProduktionsomradeDetailValidation } from "./produktionsomrade-detail.validation";

@Component({
    selector: "rask-produktionsomrade-detail",
    templateUrl: "./produktionsomrade-detail.component.html",
    styleUrls: ["./produktionsomrade-detail.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        HeaderComponent,
        InputNumberComponent,
        InputComponent,
        SelectorComponent,
        SaveButtonComponent,
        ButtonComponent,
        TranslatePipe,
        CheckboxComponent
    ]
})
export class ProduktionsomradeDetailComponent implements OnInit {
    form: UntypedFormGroup;
    id: number;
    initialized = false;
    isCalculating = false;
    isEditableRole: boolean;
    isModalActive = false;
    prislistor: IMarknadsprislista[] = [];
    produktionsomrade: ProduktionsomradeDetailDTO;
    texts = ProduktionsomradeTexts.texts;
    validation: IValidationScope;

    private readonly activeModal = inject(NgbActiveModal);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly dirtyFormService = inject(DirtyFormService);
    private readonly formBinder = inject(FormBinderService);
    private readonly marknadsprislistaService = inject(MarknadsprislistaService);
    private readonly produktionsomradeService = inject(ProduktionsomradeService);
    private readonly toastService = inject(ToastService);

    private validationContext: IValidationContext;

    get canSave(): boolean {
        return this.form.dirty && this.form.valid && !this.isCalculating && this.isEditableRole;
    }

    get title(): string {
        return `${this.texts.produktionsomrade} ${this.produktionsomrade.namn}`;
    }

    dismissModal = (): void => {
        if (this.dirtyFormService.canClose()) {
            this.activeModal.dismiss();
        }
    };

    getCurrentPrislista = (id?: number) => ({
        id: this.produktionsomrade.terminalprislistaId,
        violId: this.produktionsomrade.terminalprislistaViolId
    });

    async ngOnInit(): Promise<void> {
        this.isEditableRole = this.authenticationService.canEdit();
        if (!ObjectUtils.isNullOrUndefined(this.id)) {
            this.busyService.addWork(this.produktionsomradeService.getProduktionsomradeById(this.id))
                .pipe(
                    tap(produktionsomrade => {
                        this.produktionsomrade = produktionsomrade;
                        this.setupForm();
                        this.dirtyFormService.onDirty(this.form, this.save.bind(this), d => this.dirtyCheckCallback(d));
                    }),
                    switchMap(() => this.marknadsprislistaService.searchMarknadsprislistor({
                        typ: MarknadsprislistaTyp.Viol3,
                        resultatomraden: [this.produktionsomrade.resultatomradeId],
                        handelssortimentsgrupper: [],
                        isAktuell: true,
                        terminalprislista: true
                    }))
                )
                .subscribe(prislistor => {
                    this.prislistor = prislistor;
                    this.initialized = true;
                });
        }
    }

    prislistaFormatter = (result: IMarknadsprislista) => result ? result.id : undefined;

    prislistaViewFormatter = (result: IMarknadsprislista) => result ? result.violId : undefined;

    raknaOmDirektutbyte = async () => {
        this.isCalculating = true;
        this.busyService.addWork(this.produktionsomradeService.recalculateDirektutbyteForProduktionsomrade(this.produktionsomrade.id))
            .subscribe(p => {
                this.toastService.add(StringUtils.formatString(this.texts.raknaOmDirektutbyteResponse, p.atgarderToRecalculateCount), 1, 10000);
                this.isCalculating = false;
            });
    };

    async save(): Promise<void> {
        if (this.canSave) {
            this.busyService.addWork(this.produktionsomradeService.updateProduktionsomrade(this.produktionsomrade.id, this.produktionsomrade))
                .subscribe(p => {
                    this.toastService.add(this.texts.sparaMessage, 1, 3000);
                    this.form.markAsPristine();
                });
        }
    }

    private readonly dirtyCheckCallback = (result: IDirtyFormResult): void => {

        if (result.isSkipSave) {
            if (this.isModalActive) {
                this.activeModal.dismiss();
            }
        }

        if (result.isSave) {
            if (this.isModalActive) {
                this.activeModal.dismiss();
            }
        }
    };

    private readonly onFormChanges = (): void => {
        this.validationContext.validate();
    };

    private readonly setupForm = (): void => {
        this.form = this.formBinder.CreateGroup(this.produktionsomrade);
        this.form.valueChanges.subscribe(this.onFormChanges);

        this.validationContext = ProduktionsomradeDetailValidation.init(this.produktionsomrade);
        this.validationContext
            .scope(ValidationScopes.spara)
            .subscribe(v => { this.validation = v; });
        this.formBinder.attachValidation(this.validationContext.current, this.form);

        this.form.markAsPristine();
        this.validationContext.validate();
    };
}
